import {FC} from 'react'
import {Modal} from 'react-bootstrap'
import {toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import {formatNumber} from '../core/printNumbers'
import {
  classicPrintPageUrl,
  InvoicePrintPage,
  InvoicePrintPreference,
  newPrintPageUrl,
  PRINT_PAGE_TITLES,
} from '../core/printPagePreference'
import {PrintPageChoice} from './PrintPageChoice'
import './printPageIntro.scss'

export const PRINT_PAGE_INTRO_IMAGE = '/media/print-page/new-print-page.jpg'

type Feature = {icon: string; title: string; text: string}

const EDITOR_FEATURES: Feature[] = [
  {
    icon: 'la-stamp',
    title: 'مهر و امضای خوانا',
    text: 'اندازهٔ مهر را خودتان تعیین کنید؛ در چاپ افقی هم کوچک و ناخوانا نمی‌شود.',
  },
  {
    icon: 'la-crop',
    title: 'ویرایش تصویر لوگو و مهر',
    text: 'برش، چرخش، صاف‌کردن، شفاف‌کردن زمینهٔ کاغذ و حذف خودکار حاشیه، بدون نرم‌افزار دیگر.',
  },
  {
    icon: 'la-palette',
    title: 'ظاهر برگه',
    text: 'اندازهٔ لوگو، رنگ، جهت برگه، کادر امضای خریدار و چاپ مهر به‌طور پیش‌فرض.',
  },
  {
    icon: 'la-align-right',
    title: 'متن ثابت توضیحات',
    text: 'شماره حساب، شبا یا هر متن ثابت دیگر، پیش از توضیح هر صورتحساب چاپ می‌شود.',
  },
]

/** What the announcement offers about the designer, from api/print-templates. */
export type PrintTemplateEditorEntry =
  | {kind: 'open'; url: string}
  | {kind: 'readOnly'; reason: string | null}
  | {kind: 'notReady'}
  | {kind: 'unknown'}

const READ_ONLY_EDITOR_NOTE =
  'ویرایش قالب چاپ برای شما فعال نیست. مدیر کسب‌وکار می‌تواند دسترسی «ویرایش و افزودن قالب چاپ» را در «کاربران و دسترسی‌ها» برایتان فعال کند.'

const FEATURES: Feature[] = [
  {
    icon: 'la-link',
    title: 'بدون ورود به پنل',
    text: 'خریدار با لینک صورتحساب یا QR کد روی کاغذ، آن را می‌بیند و چاپ می‌کند.',
  },
  {
    icon: 'la-copy',
    title: 'صفحه‌بندی خودکار A4',
    text: 'در صورتحساب‌های پرردیف، پایین هر برگه «جمع این صفحه» و «جمع کل تا این صفحه» می‌آید.',
  },
  {
    icon: 'la-th-list',
    title: 'ستون‌های همان الگو',
    text: 'فقط اطلاعات نوع و الگوی همان صورتحساب، طبق دستورالعمل سامانه مؤدیان.',
  },
  {
    icon: 'la-mobile',
    title: 'موبایل و PDF',
    text: 'نسخهٔ خوانا روی گوشی و دریافت فایل PDF از همان صفحه.',
  },
]

type Props = {
  show: boolean
  businessName: string
  preference: InvoicePrintPreference
  selected: InvoicePrintPage
  onSelect: (page: InvoicePrintPage) => void
  dontShowAgain: boolean
  onDontShowAgain: (value: boolean) => void
  saving: boolean
  error?: string
  editor: PrintTemplateEditorEntry
  onOpenEditor: (url: string) => void
  onConfirm: () => void
  onClose: () => void
}

const FeatureList: FC<{features: Feature[]; label: string}> = ({features, label}) => (
  <ul className='ppi-features' aria-label={label}>
    {features.map((feature) => (
      <li key={feature.title} className='ppi-feature'>
        <span className='ppi-feature__icon' aria-hidden='true'>
          <i className={`las ${feature.icon}`} />
        </span>
        <span>
          <strong>{feature.title}</strong>
          <span className='ppi-feature__text'>{feature.text}</span>
        </span>
      </li>
    ))}
  </ul>
)

/**
 * The announcement of the print-template designer and the new print page: what changed, a way into
 * the designer for whoever may edit templates, the business's default page (changeable here by
 * whoever may change the business's settings) and the business's newest invoice with either page.
 * «دیگر نمایش نده» keeps it closed for good.
 */
export const PrintPageIntroModal: FC<Props> = ({
  show,
  businessName,
  preference,
  selected,
  onSelect,
  dontShowAgain,
  onDontShowAgain,
  saving,
  error,
  editor,
  onOpenEditor,
  onConfirm,
  onClose,
}) => {
  const latest = preference.latestInvoice
  const changed = selected !== preference.page
  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='lg'
      fullscreen='sm-down'
      scrollable
      dialogClassName='ppi-modal'
      aria-labelledby='ppi-title'
    >
      <Modal.Body className='ppi-modal__body'>
        <button type='button' className='ppi-modal__close' onClick={onClose} aria-label='بستن'>
          <i className='las la-times' aria-hidden='true' />
        </button>

        <div className='ppi-hero' aria-hidden='true'>
          <span className='ppi-hero__glow' />
          <span className='ppi-hero__sheet ppi-hero__sheet--far' />
          <span className='ppi-hero__sheet ppi-hero__sheet--near' />
          <span className='ppi-hero__shot'>
            <img src={toAbsoluteUrl(PRINT_PAGE_INTRO_IMAGE)} alt='' />
            <span className='ppi-hero__sweep' />
          </span>
          <span className='ppi-hero__chip ppi-hero__chip--a4'>
            <i className='las la-file-alt' />
            A4
          </span>
          <span className='ppi-hero__chip ppi-hero__chip--qr'>
            <i className='las la-qrcode' />
            بدون ورود
          </span>
          <span className='ppi-hero__chip ppi-hero__chip--total'>
            <i className='las la-calculator' />
            جمع هر صفحه
          </span>
          <span className='ppi-hero__chip ppi-hero__chip--studio'>
            <i className='las la-magic' />
            ویرایشگر قالب
          </span>
        </div>

        <div className='ppi-content'>
          <span className='ppi-eyebrow'>
            <i className='las la-star' aria-hidden='true' />
            تازه
          </span>
          <h2 id='ppi-title' className='ppi-title'>
            ویرایشگر تازهٔ قالب چاپ
          </h2>
          <p className='ppi-lead'>
            لوگو، مهر و امضا و ظاهر برگه را خودتان تنظیم کنید و هر تغییر را همان لحظه روی آخرین
            صورتحساب کسب‌وکار، در صفحهٔ چاپ جدید و صفحهٔ قبلی، ببینید. تا «ذخیره» را نزنید، چاپ
            صورتحساب‌ها تغییری نمی‌کند.
          </p>

          <FeatureList features={EDITOR_FEATURES} label='امکانات ویرایشگر قالب چاپ' />

          <div className='ppi-cta'>
            {editor.kind === 'open' ? (
              <>
                <button
                  type='button'
                  className='btn btn-light-primary'
                  onClick={() => onOpenEditor(editor.url)}
                  disabled={saving}
                >
                  <i className='las la-magic' aria-hidden='true' />
                  باز کردن ویرایشگر قالب چاپ
                </button>
                <span className='ppi-note'>
                  بعداً هم از «تنظیمات کسب‌وکار › قالب‌های چاپ» باز می‌شود.
                </span>
              </>
            ) : editor.kind === 'readOnly' ? (
              <p className='ppi-note ppi-note--readonly'>
                {editor.reason || READ_ONLY_EDITOR_NOTE}
              </p>
            ) : editor.kind === 'notReady' ? (
              <p className='ppi-note'>
                ذخیرهٔ طراحی قالب تا چند دقیقهٔ دیگر فعال می‌شود؛ ویرایشگر از «تنظیمات کسب‌وکار ›
                قالب‌های چاپ» باز می‌شود.
              </p>
            ) : (
              <p className='ppi-note'>ویرایشگر از «تنظیمات کسب‌وکار › قالب‌های چاپ» باز می‌شود.</p>
            )}
          </div>

          <section className='ppi-section' aria-labelledby='ppi-page-title'>
            <h3 id='ppi-page-title' className='ppi-section__title'>
              صفحهٔ چاپ جدید
            </h3>
            <p className='ppi-note'>
              صورتحساب‌ها با صفحهٔ چاپ جدید باز، چاپ و PDF می‌شوند. صفحهٔ قبلی هم کنار آن می‌ماند و
              از منوی هر صورتحساب در دسترس است.
            </p>
            <FeatureList features={FEATURES} label='امکانات صفحهٔ چاپ جدید' />
          </section>

          <section className='ppi-section' aria-labelledby='ppi-default-title'>
            <h3 id='ppi-default-title' className='ppi-section__title'>
              صفحهٔ چاپ پیش‌فرض «{businessName}»
            </h3>
            {preference.canChange ? (
              <>
                <PrintPageChoice
                  name='ppi-page'
                  value={selected}
                  onChange={onSelect}
                  disabled={saving}
                />
                <p className='ppi-note'>
                  این انتخاب برای همهٔ کاربران این کسب‌وکار اعمال می‌شود و بعداً هم از «تنظیمات
                  کسب‌وکار › قالب‌های چاپ» قابل تغییر است.
                </p>
              </>
            ) : (
              <p className='ppi-note ppi-note--readonly'>
                صفحهٔ چاپ این کسب‌وکار: <strong>{PRINT_PAGE_TITLES[preference.page]}</strong>. تغییر
                آن با کاربرانی است که به تنظیمات کسب‌وکار دسترسی دارند.
              </p>
            )}
          </section>

          <section className='ppi-section' aria-labelledby='ppi-latest-title'>
            <h3 id='ppi-latest-title' className='ppi-section__title'>
              آخرین صورتحساب خود را ببینید
            </h3>
            {latest ? (
              <>
                <p className='ppi-latest'>
                  {latest.number ? (
                    <>
                      صورتحساب شمارهٔ <bdi>{latest.number}</bdi>،
                    </>
                  ) : (
                    'صورتحساب'
                  )}{' '}
                  تاریخ <bdi>{latest.issuedAt}</bdi>، مبلغ <bdi>{formatNumber(latest.amount)}</bdi>{' '}
                  ریال
                </p>
                <div className='ppi-links'>
                  <a
                    className='btn btn-sm btn-light-primary'
                    href={newPrintPageUrl(latest.hashCode)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <i className='las la-external-link-alt' aria-hidden='true' />
                    مشاهده با {PRINT_PAGE_TITLES.new}
                  </a>
                  <a
                    className='btn btn-sm btn-light'
                    href={classicPrintPageUrl(latest.hashCode)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <i className='las la-external-link-alt' aria-hidden='true' />
                    مشاهده با {PRINT_PAGE_TITLES.classic}
                  </a>
                </div>
              </>
            ) : (
              <p className='ppi-note'>
                هنوز صورتحسابی در این کسب‌وکار ثبت نشده است؛ نخستین صورتحساب با صفحهٔ چاپ پیش‌فرض
                باز می‌شود.
              </p>
            )}
          </section>

          {error && (
            <div className='alert alert-danger py-3 mb-0' role='alert'>
              {error}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className='ppi-modal__footer'>
        <label className='form-check form-check-custom form-check-solid ppi-dismiss'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={dontShowAgain}
            onChange={(event) => onDontShowAgain(event.target.checked)}
          />
          <span className='form-check-label'>دیگر نمایش نده</span>
        </label>
        <button type='button' className='btn btn-primary' onClick={onConfirm} disabled={saving}>
          {saving && <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />}
          {changed && preference.canChange ? 'ذخیرهٔ انتخاب و بستن' : 'متوجه شدم'}
        </button>
      </Modal.Footer>
    </Modal>
  )
}
